/**
 * Requirements View Store: per-project UI state for the Requirements module.
 *
 * Owns:
 *   - view (document | draft | table)
 *   - groupBy (hierarchy | persona | theme | status | priority)
 *   - sidebarCollapsed
 *   - notesLevel (outline | summary | full): used in document view
 *   - notesCollapsedKeys: collapsed state per epic in document view
 *   - expandedStoryIds: inline detail expansion, shared across views
 *   - search + facet selections (Set<string>)
 *
 * View names: 'document' = long-form PRD, 'draft' = quick capture, 'table' = spreadsheet. Older persisted
 * values 'list'/'tree'/'notes' all migrate to 'document' on hydrate (v4 removed tree).
 *
 * Persistence: per-project via `persist` middleware with a Set-aware
 * storage codec so Set<string> round-trips as Array<string>.
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export type RequirementsView = 'outline' | 'document' | 'draft' | 'table'
export type RequirementsGroupBy = 'hierarchy' | 'persona' | 'theme' | 'status' | 'priority'
export type NotesLevel = 'outline' | 'summary' | 'full'

// Map legacy view names from prior versions of this store. The 'tree' view
// was removed in v4, so callers fall back to 'outline' (the new default).
function coerceView(v: unknown): RequirementsView {
  if (v === 'outline' || v === 'document' || v === 'draft' || v === 'table') return v
  if (v === 'notes') return 'document'
  // 'tree' (v3) and 'list' (legacy v2) map to 'outline'.
  return 'outline'
}

interface RequirementsViewState {
  projectId: string | null
  view: RequirementsView
  groupBy: RequirementsGroupBy
  sidebarCollapsed: boolean
  notesLevel: NotesLevel
  notesCollapsedKeys: Set<string>  // 'i:<id>' | 'e:<id>'
  expandedStoryIds: Set<string>
  searchQuery: string
  facetTypes: Set<string>
  facetPriorities: Set<string>
  facetStatuses: Set<string>
  facetThemeIds: Set<string>
  /** View scope: when set, narrows the working set to this requirement and its descendants. */
  scopeId: string | null
  /** Transient: requirement id that should auto-focus its title on next mount (e.g. a just-created block). Cleared after focus. */
  focusBlockId: string | null
  /**
   * Transient: requirement id that should be opened in the detail drawer
   * after the next view change. Set by the Document-view zoom-to-row
   * affordance and consumed by RequirementsPage. Cleared once handled.
   */
  pendingDetailId: string | null

  // Actions
  setProject: (projectId: string) => void
  setView: (view: RequirementsView) => void
  setGroupBy: (groupBy: RequirementsGroupBy) => void
  toggleSidebar: () => void
  setNotesLevel: (level: NotesLevel) => void
  toggleNotesCollapse: (key: string) => void
  toggleExpanded: (storyId: string) => void
  setSearchQuery: (query: string) => void
  toggleFacet: (facet: 'types' | 'priorities' | 'statuses' | 'themeIds', value: string) => void
  clearFacets: () => void
  scopeTo: (id: string | null) => void
  setFocusBlockId: (id: string | null) => void
  setPendingDetailId: (id: string | null) => void
  reset: () => void
}

const DEFAULTS = {
  view: 'outline' as RequirementsView,
  groupBy: 'persona' as RequirementsGroupBy,
  sidebarCollapsed: false,
  notesLevel: 'full' as NotesLevel,
}

function makeEmptyState(projectId: string | null): Omit<
  RequirementsViewState,
  'setProject' | 'setView' | 'setGroupBy' | 'toggleSidebar' | 'setNotesLevel'
    | 'toggleNotesCollapse' | 'toggleExpanded' | 'setSearchQuery' | 'toggleFacet' | 'clearFacets' | 'scopeTo' | 'setFocusBlockId' | 'setPendingDetailId' | 'reset'
> {
  return {
    projectId,
    view: DEFAULTS.view,
    groupBy: DEFAULTS.groupBy,
    sidebarCollapsed: DEFAULTS.sidebarCollapsed,
    notesLevel: DEFAULTS.notesLevel,
    notesCollapsedKeys: new Set(),
    expandedStoryIds: new Set(),
    searchQuery: '',
    facetTypes: new Set(),
    facetPriorities: new Set(),
    facetStatuses: new Set(),
    facetThemeIds: new Set(),
    scopeId: null,
    focusBlockId: null,
    pendingDetailId: null,
  }
}

// ============================================================================
// SET-AWARE STORAGE: converts Set<string> to Array<string> on serialize.
// ============================================================================

const storage = createJSONStorage(() => localStorage, {
  replacer: (_key, value) => {
    if (value instanceof Set) return { __type: 'Set', values: Array.from(value) }
    return value
  },
  reviver: (_key, value) => {
    if (value && typeof value === 'object' && (value as { __type?: string }).__type === 'Set') {
      return new Set((value as { values: string[] }).values)
    }
    return value
  },
})

// ============================================================================
// STORE: keyed by projectId. We use a single store per mount and reset its
// data when the project switches (setProject).
// ============================================================================

export const useRequirementsViewStore = create<RequirementsViewState>()(
  persist(
    (set) => ({
      ...makeEmptyState(null),

      setProject: (projectId) => set((s) => {
        if (s.projectId === projectId) return s
        return { ...makeEmptyState(projectId) }
      }),

      setView: (view) => set({ view }),
      setGroupBy: (groupBy) => set({ groupBy }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setNotesLevel: (level) => set((s) => {
        // Setting a level rewrites notesCollapsedKeys; per-heading state still
        // overrides (user can expand one in Outline by tapping its chevron).
        return { notesLevel: level, notesCollapsedKeys: new Set() }
      }),
      toggleNotesCollapse: (key) => set((s) => {
        const next = new Set(s.notesCollapsedKeys)
        if (next.has(key)) next.delete(key); else next.add(key)
        return { notesCollapsedKeys: next }
      }),
      toggleExpanded: (storyId) => set((s) => {
        const next = new Set(s.expandedStoryIds)
        if (next.has(storyId)) next.delete(storyId); else next.add(storyId)
        return { expandedStoryIds: next }
      }),
      setSearchQuery: (query) => set({ searchQuery: query }),
      toggleFacet: (facet, value) => set((s) => {
        const key = (`facet${facet.charAt(0).toUpperCase() + facet.slice(1)}`) as
          'facetTypes' | 'facetPriorities' | 'facetStatuses' | 'facetThemeIds'
        const current = s[key] as Set<string>
        const next = new Set(current)
        if (next.has(value)) next.delete(value); else next.add(value)
        return { [key]: next } as Partial<RequirementsViewState>
      }),
      clearFacets: () => set({
        facetTypes: new Set(),
        facetPriorities: new Set(),
        facetStatuses: new Set(),
        facetThemeIds: new Set(),
      }),
      scopeTo: (id) => set({ scopeId: id }),
      setFocusBlockId: (id) => set({ focusBlockId: id }),
      setPendingDetailId: (id) => set({ pendingDetailId: id }),
      reset: () => set((s) => makeEmptyState(s.projectId)),
    }),
    {
      name: 'requirements-view',
      storage,
      // Persist only the preferences that should survive reloads. Expansion is
      // session-only (doesn't snowball across days).
      partialize: (s) => ({
        projectId: s.projectId,
        view: s.view,
        groupBy: s.groupBy,
        sidebarCollapsed: s.sidebarCollapsed,
        notesLevel: s.notesLevel,
        notesCollapsedKeys: s.notesCollapsedKeys,
        // Deliberately omit: expandedStoryIds, searchQuery, facet*, focusBlockId
      }) as Partial<RequirementsViewState>,
      version: 5,
      // v4: remove 'tree' view, collapse 'tree' (and legacy 'list') to 'document'.
      // v3: rename view 'list'→'tree', 'notes'→'document'.
      // v2: coerce any Set-typed field that arrives as a non-Set.
      migrate: (persisted: unknown) => {
        const obj = (persisted ?? {}) as Record<string, unknown>
        const coerce = (v: unknown): Set<string> => {
          if (v instanceof Set) return v as Set<string>
          if (Array.isArray(v)) return new Set(v as string[])
          return new Set()
        }
        obj.notesCollapsedKeys = coerce(obj.notesCollapsedKeys)
        obj.view = coerceView(obj.view)
        return obj as Partial<RequirementsViewState>
      },
      // Merge: guarantee every Set field is a Set even if rehydration produced
      // a plain array or object, and that view is one of the v3 names.
      merge: (persisted: unknown, current: RequirementsViewState) => {
        const p = (persisted ?? {}) as Partial<RequirementsViewState>
        const setOrDefault = (v: unknown): Set<string> => {
          if (v instanceof Set) return v as Set<string>
          if (Array.isArray(v)) return new Set(v as string[])
          return new Set()
        }
        return {
          ...current,
          ...p,
          view: coerceView(p.view),
          notesCollapsedKeys: setOrDefault(p.notesCollapsedKeys),
          expandedStoryIds: new Set(),
          facetTypes: new Set(),
          facetPriorities: new Set(),
          facetStatuses: new Set(),
          facetThemeIds: new Set(),
          scopeId: null,
          focusBlockId: null,
          pendingDetailId: null,
        } as RequirementsViewState
      },
    },
  ),
)

// Convenience hooks: selectors avoid unneeded re-renders
export const useRequirementsView = () => useRequirementsViewStore((s) => s.view)
export const useRequirementsGroupBy = () => useRequirementsViewStore((s) => s.groupBy)
export const useIsStoryExpanded = (id: string) =>
  useRequirementsViewStore((s) => s.expandedStoryIds.has(id))
export const useIsNotesCollapsed = (key: string) =>
  useRequirementsViewStore((s) => s.notesCollapsedKeys.has(key))
